/**
 * Given a name, correct the casing to adhere to our convention.
 * Names returned by HCP can be ALL CAPS, so we want to correct that.
 *
 * @example capitalizeName("SHAI GILGEOUS-ALEXANDER") // returns "Shai Gilgeous-Alexander"
 */
export function capitalizeName(name: string): string {
  return name
    .toLowerCase()
    .split(" ")
    .reduce((acc, part) => {
      return `${acc} ${capitlalizeWordFirstLetter(part)}`;
    }, "")
    .split("-")
    .reduce((acc, part) => {
      // This ternary prevents a '-' from being prepended to the first split.
      // Subsequent split parts will be separated by a '-'.
      return acc === ""
        ? `${acc}${capitlalizeWordFirstLetter(part)}`
        : `${acc}-${capitlalizeWordFirstLetter(part)}`;
    }, "");
}

export function capitalizeSentenceFirstLetters(sentence: string): string {
  return sentence
    .split(" ")
    .map((word) => {
      return capitlalizeWordFirstLetter(word);
    })
    .join(" ");
}

export function capitlalizeWordFirstLetter(word: string): string {
  return word?.charAt(0)?.toUpperCase() + word?.slice(1);
}

export function toPascalCase(input: string): string {
  if (!input) return '';

  return input
    .toLowerCase()
    .split(/[\s-_/]+/) // Split by spaces, hyphens, underscores, or slashes
    .map((word) => {return word.charAt(0).toUpperCase() + word.slice(1)})
    .join(' ');
}

/**
 * UHG / Optum standard for copy is sentence case, which means that phrases
 * should be formatted "Like this, for example" and not "Like This, For Example"
 * or "like this, for example".
 * This fn can be used to convert a phrase to sentence case.
 */
export function capitalizeFirstLetter(phrase: string): string {
  return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase();
}

export function capitlalizeWordWithHyphen(str: string) {
  return str.toLowerCase().replace(/(?:^|[\s-/])\w/g, function (match) {
    return match.toUpperCase();
  });
}

/**
 * Given a string of text, convert it to kebab case.
 * @example prettyTextToKebabCase("Simon & Garfunkel"); // Returns "simon-and-garfunkel"
 * @example prettyTextToKebabCase("Either / or"); // Returns "either-or"
 */
export function prettyTextToKebabCase(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[\s\/]/g, "-")
    .replaceAll(/&/g, "and");
}

/**
 * Formats the free_or_reduced cost field by replacing underscores with spaces
 * and capitalizing each word. Returns fallback text if the input is empty/invalid.
 *
 * @param cost - The raw cost string (e.g., "free_or_reduced", "no_cost")
 * @param fallback - Fallback text when cost is empty/invalid (default: "No pricing information is available")
 * @returns Formatted cost string (e.g., "Free Or Reduced", "No Cost")
 *
 * @example formatCostField("free_or_reduced") // Returns "Free Or Reduced"
 * @example formatCostField("no_cost") // Returns "No Cost"
 * @example formatCostField("") // Returns "No pricing information is available"
 * @example formatCostField(null, "N/A") // Returns "N/A"
 */
export function formatCostField(
  cost?: string | null,
  fallback: string = 'No pricing information is available'
): string {
  if (!cost || cost.trim() === '') {
    return fallback;
  }

  return capitalizeSentenceFirstLetters(cost.replace(/_/g, ' '));
}
